/**
 * Stock Chart Renderer
 * 
 * Renders a professional stock chart with:
 * - Line chart with price over time
 * - Time period buttons (1D, 5D, 1M, 6M, YTD, 1Y, 5Y, MAX)
 * - Current price display with change/percentage
 * - After hours pricing
 * - Financial metrics (Open, Volume, Day Low/High, Year Low/High)
 * - Interactive time range selection
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

interface RenderDimensions {
  width: number;
  height: number;
}

interface StockDataPoint {
  timestamp: number; // Unix timestamp in milliseconds
  price: number;
  volume?: number;
}

interface StockChartData {
  symbol: string;
  companyName?: string;
  currentPrice: number;
  previousClose: number;
  change: number;
  changePercent: number;
  afterHoursPrice?: number;
  afterHoursChange?: number;
  afterHoursChangePercent?: number;
  open: number;
  volume: number;
  dayLow: number;
  dayHigh: number;
  yearLow: number;
  yearHigh: number;
  dataPoints: StockDataPoint[]; // Historical price data
  selectedPeriod?: '1D' | '5D' | '1M' | '6M' | 'YTD' | '1Y' | '5Y' | 'MAX';
}

function renderEmptyState(message: string): string {
  return `
    <div style="
      display: flex;
      align-items: center;
      justify-content: center;
      height: 400px;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.md};
    ">
      ${escapeHtml(message)}
    </div>
  `;
}

export const renderStockChart: VisualizationRenderer = (data, meta, layout) => {
  // Extract viewId from meta if available (for event handling)
  const viewId = (meta as any)?.viewId || '';
  
  // Get dimensions from layout or use defaults
  // Layout can be an object with width/height or a string
  let containerWidth = 800;
  let containerHeight = 400;
  let widthMode: 'standard' | 'double' | undefined;
  let heightMode: 'full' | 'half' | undefined;
  
  if (layout && typeof layout === 'object' && 'width' in layout && 'height' in layout) {
    const layoutWidth = layout.width as number;
    const layoutHeight = layout.height as number;
    // Only use layout dimensions if they're valid (greater than 0)
    if (layoutWidth > 0 && layoutHeight > 0) {
      containerWidth = layoutWidth;
      containerHeight = layoutHeight;
    }
    // Check for widthMode and heightMode in layout (passed from VisualizationView)
    if ('widthMode' in layout) {
      widthMode = layout.widthMode as 'standard' | 'double' | undefined;
    }
    if ('heightMode' in layout) {
      heightMode = layout.heightMode as 'full' | 'half' | undefined;
    }
  }
  
  // Only show chart in state 3 (double width + full height)
  const shouldShowChart = widthMode === 'double' && heightMode === 'full';
  
  // Calculate available space (accounting for padding and header)
  const containerPadding = typeof designTokens.padding.md === 'number' ? designTokens.padding.md : 16;
  const headerHeight = 120; // Approximate header height
  const metricsHeight = 120; // Approximate metrics section height
  const periodButtonsHeight = 40;
  const paddingTotal = containerPadding * 4;
  
  // Chart dimensions (only calculate if showing chart)
  let chartWidth = 0;
  let chartHeight = 0;
  let padding = { top: 0, right: 0, bottom: 0, left: 0 };
  let actualChartWidth = 0;
  let actualChartHeight = 0;
  let width = 0;
  let height = 0;
  let linePath = '';
  let horizontalGridLines = '';
  let timeLabels = '';
  
  if (shouldShowChart) {
    const availableHeight = Math.max(0, containerHeight - headerHeight - metricsHeight - periodButtonsHeight - paddingTotal);
    const availableWidth = Math.max(0, containerWidth - (containerPadding * 2));
    
    // Responsive dimensions - ensure minimum sizes
    const minChartWidth = 300;
    const minChartHeight = 150;
    chartWidth = Math.max(minChartWidth, availableWidth - 40);
    chartHeight = Math.max(minChartHeight, availableHeight);
    
    // Responsive padding based on chart size
    padding = {
      top: Math.max(40, chartHeight * 0.1),
      right: Math.max(20, chartWidth * 0.05),
      bottom: Math.max(40, chartHeight * 0.1),
      left: Math.max(50, chartWidth * 0.1)
    };
    
    actualChartWidth = chartWidth - padding.left - padding.right;
    actualChartHeight = chartHeight - padding.top - padding.bottom;
    width = chartWidth;
    height = chartHeight;
  }
  // Expected data format: StockChartData object
  if (!data || typeof data !== 'object') {
    return renderEmptyState('No stock data to display');
  }

  const stockData = data as StockChartData;
  
  // Validate required fields
  if (!stockData.symbol || stockData.currentPrice === undefined || !Array.isArray(stockData.dataPoints)) {
    return renderEmptyState('Invalid stock data format');
  }

  const symbol = stockData.symbol;
  const companyName = stockData.companyName || symbol;
  const currentPrice = stockData.currentPrice;
  const change = stockData.change || 0;
  const changePercent = stockData.changePercent || 0;
  const isPositive = change >= 0;
  const changeColor = isPositive ? '#00c853' : '#ff3b30';
  
  // After hours data
  const afterHoursPrice = stockData.afterHoursPrice;
  const afterHoursChange = stockData.afterHoursChange;
  const afterHoursChangePercent = stockData.afterHoursChangePercent;
  
  // Financial metrics
  const open = stockData.open;
  const volume = stockData.volume;
  const dayLow = stockData.dayLow;
  const dayHigh = stockData.dayHigh;
  const yearLow = stockData.yearLow;
  const yearHigh = stockData.yearHigh;
  
  // Chart data
  const dataPoints = stockData.dataPoints || [];
  const selectedPeriod = stockData.selectedPeriod || '1D';
  
  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };
  
  // Format percentage
  const formatPercent = (percent: number) => {
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };
  
  // Format volume
  const formatVolume = (vol: number) => {
    if (vol >= 1000000) {
      return `${(vol / 1000000).toFixed(1)}M`;
    } else if (vol >= 1000) {
      return `${(vol / 1000).toFixed(1)}K`;
    }
    return vol.toString();
  };
  
  // Format date for chart
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };
  
  // Get current date for display
  const currentDate = new Date();
  const dateStr = currentDate.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric' 
  });
  
  // Responsive font sizes based on available space (calculate early, before use)
  // Use container dimensions for compact check if chart not shown
  const isCompact = shouldShowChart ? (chartWidth < 500 || chartHeight < 250) : (containerWidth < 500 || containerHeight < 250);
  const headerFontSize = isCompact ? designTokens.fontSize.lg : designTokens.fontSize.xl;
  const priceFontSize = isCompact ? '20px' : '28px';
  const symbolFontSize = isCompact ? designTokens.fontSize.xs : designTokens.fontSize.sm;
  const changeFontSize = isCompact ? designTokens.fontSize.sm : designTokens.fontSize.md;
  const periodButtonFontSize = isCompact ? designTokens.fontSize.xs : designTokens.fontSize.sm;
  const periodButtonPadding = isCompact ? '3px 6px' : '4px 8px';
  
  // Mini-chart for header (when NOT in state 3) - calculate after dataPoints is defined
  let miniChartHTML = '';
  if (!shouldShowChart && dataPoints.length > 0) {
    // Calculate price range for mini-chart
    const prices = dataPoints.map(p => p.price);
    const minPrice = Math.min(...prices, dayLow);
    const maxPrice = Math.max(...prices, dayHigh);
    const priceRange = maxPrice - minPrice;
    const pricePadding = priceRange * 0.05; // Small padding for mini-chart
    
    // Mini-chart dimensions - responsive to container width
    // Calculate available width for mini-chart (container width minus text content and padding)
    const textContentWidth = 350; // Approximate width for price/change/date text
    const availableMiniWidth = Math.max(120, containerWidth - textContentWidth - (containerPadding * 2) - 60);
    // Use a base width for viewBox calculation, but let SVG stretch to fill available space
    const miniChartBaseWidth = Math.min(Math.max(120, availableMiniWidth), 500); // Base width for viewBox, cap at 500px
    const miniChartHeight = 40; // Fixed small height
    const miniPadding = 4; // Minimal padding
    
    // Scale functions for mini-chart (use base width for calculations)
    const miniScaleX = (index: number) => {
      if (dataPoints.length === 0) return miniPadding;
      return miniPadding + (index / (dataPoints.length - 1)) * (miniChartBaseWidth - miniPadding * 2);
    };
    
    const miniScaleY = (price: number) => {
      const normalizedPrice = (price - minPrice + pricePadding) / (priceRange + pricePadding * 2);
      return miniPadding + (miniChartHeight - miniPadding * 2) - (normalizedPrice * (miniChartHeight - miniPadding * 2));
    };
    
    // Generate mini line path
    const miniPoints = dataPoints.map((point, index) => {
      const x = miniScaleX(index);
      const y = miniScaleY(point.price);
      return `${x},${y}`;
    });
    const miniLinePath = `M ${miniPoints.join(' L ')}`;
    
    miniChartHTML = `
      <svg
        width="100%"
        height="${miniChartHeight}"
        viewBox="0 0 ${miniChartBaseWidth} ${miniChartHeight}"
        preserveAspectRatio="none"
        style="
          min-width: 120px;
          opacity: 0.7;
        "
      >
        <path
          d="${miniLinePath}"
          fill="none"
          stroke="${changeColor}"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }
  
  // Chart calculations (only if showing full chart)
  if (shouldShowChart) {
    // Calculate price range
    const prices = dataPoints.map(p => p.price);
    const minPrice = Math.min(...prices, dayLow);
    const maxPrice = Math.max(...prices, dayHigh);
    const priceRange = maxPrice - minPrice;
    const pricePadding = priceRange * 0.1; // 10% padding
    
    // Scale functions - use actual chart dimensions
    const scaleX = (index: number) => {
      if (dataPoints.length === 0) return padding.left;
      return padding.left + (index / (dataPoints.length - 1)) * actualChartWidth;
    };
    
    const scaleY = (price: number) => {
      const normalizedPrice = (price - minPrice + pricePadding) / (priceRange + pricePadding * 2);
      return padding.top + actualChartHeight - (normalizedPrice * actualChartHeight);
    };
    
    // Generate line path
    if (dataPoints.length > 0) {
      const points = dataPoints.map((point, index) => {
        const x = scaleX(index);
        const y = scaleY(point.price);
        return `${x},${y}`;
      });
      linePath = `M ${points.join(' L ')}`;
    }
    
    // Generate grid lines
    const gridLines = 5;
    horizontalGridLines = Array.from({ length: gridLines + 1 }, (_, i) => {
      const price = minPrice - pricePadding + ((priceRange + pricePadding * 2) / gridLines) * i;
      const y = scaleY(price);
      return `
        <line
          x1="${padding.left}"
          y1="${y}"
          x2="${padding.left + actualChartWidth}"
          y2="${y}"
          stroke="${designTokens.borderColor}"
          stroke-width="1"
          opacity="0.3"
        />
        <text
          x="${padding.left - 10}"
          y="${y + 4}"
          text-anchor="end"
          fill="${designTokens.contentColorMuted}"
          font-size="${designTokens.fontSize.xs}"
        >
          ${formatPrice(price)}
        </text>
      `;
    }).join('');
    
    // Generate time axis labels
    timeLabels = dataPoints.length > 0 ? (() => {
      const labelCount = 5;
      const step = Math.max(1, Math.floor(dataPoints.length / labelCount));
      return dataPoints
        .filter((_, index) => index % step === 0 || index === dataPoints.length - 1)
        .map((point, index) => {
          const x = scaleX(dataPoints.indexOf(point));
          return `
            <text
              x="${x}"
              y="${padding.top + actualChartHeight + 20}"
              text-anchor="middle"
              fill="${designTokens.contentColorMuted}"
              font-size="${designTokens.fontSize.xs}"
            >
              ${formatTime(point.timestamp)}
            </text>
          `;
        })
        .join('');
    })() : '';
  }
  
  // Determine available periods based on data range
  const getAvailablePeriods = (): Array<'1D' | '5D' | '1M' | '6M' | 'YTD' | '1Y' | '5Y' | 'MAX'> => {
    if (dataPoints.length === 0) {
      return ['1D']; // Default to 1D if no data
    }
    
    const now = Date.now();
    const oldestTimestamp = Math.min(...dataPoints.map(p => p.timestamp));
    const dataAgeMs = now - oldestTimestamp;
    const dataAgeDays = dataAgeMs / (1000 * 60 * 60 * 24);
    
    const available: Array<'1D' | '5D' | '1M' | '6M' | 'YTD' | '1Y' | '5Y' | 'MAX'> = [];
    
    // Always include 1D
    available.push('1D');
    
    // Add periods based on available data
    if (dataAgeDays >= 5) available.push('5D');
    if (dataAgeDays >= 30) available.push('1M');
    if (dataAgeDays >= 180) available.push('6M');
    if (dataAgeDays >= getDaysSinceYearStart()) available.push('YTD');
    if (dataAgeDays >= 365) available.push('1Y');
    if (dataAgeDays >= 1825) available.push('5Y');
    if (dataAgeDays >= 365) available.push('MAX'); // MAX if we have at least a year
    
    return available;
  };
  
  // Helper to get days since year start
  const getDaysSinceYearStart = (): number => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    return (now.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24);
  };
  
  // Time period buttons - only show available periods
  const allPeriods: Array<'1D' | '5D' | '1M' | '6M' | 'YTD' | '1Y' | '5Y' | 'MAX'> = 
    ['1D', '5D', '1M', '6M', 'YTD', '1Y', '5Y', 'MAX'];
  const availablePeriods = getAvailablePeriods();
  
  const periodButtons = allPeriods
    .filter(period => availablePeriods.includes(period))
    .map(period => `
      <button
        class="stock-chart-period-btn"
        data-period="${period}"
        data-view-id="${meta?.viewId || ''}"
        style="
          padding: ${periodButtonPadding};
          border: 1px solid ${designTokens.borderColor};
          background: ${selectedPeriod === period ? 'rgba(102, 204, 255, 0.2)' : 'transparent'};
          color: ${selectedPeriod === period ? designTokens.accent : designTokens.contentColorMuted};
          border-radius: 4px;
          font-size: ${periodButtonFontSize};
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        "
        onmouseover="this.style.background='rgba(102, 204, 255, 0.1)'"
        onmouseout="this.style.background='${selectedPeriod === period ? 'rgba(102, 204, 255, 0.2)' : 'transparent'}'"
      >
        ${period}
      </button>
    `).join('');
  
  return `
    <div class="stock-chart-container" style="
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    ">
      <!-- Header: Symbol and Price -->
      <div style="
        margin-bottom: ${isCompact ? '8px' : '12px'};
        flex-shrink: 0;
      ">
        <div style="
          display: flex;
          align-items: baseline;
          gap: ${isCompact ? '8px' : '12px'};
          margin-bottom: ${isCompact ? '4px' : '8px'};
          flex-wrap: wrap;
        ">
          <h2 style="
            margin: 0;
            font-size: ${headerFontSize};
            font-weight: 600;
            color: ${designTokens.contentColor};
            line-height: 1.2;
          ">
            ${escapeHtml(companyName)}
          </h2>
          <span style="
            color: ${designTokens.contentColorMuted};
            font-size: ${symbolFontSize};
          ">
            ${escapeHtml(symbol)}
          </span>
        </div>
        <div style="
          display: flex;
          align-items: center;
          gap: ${isCompact ? '8px' : '12px'};
          flex-wrap: nowrap;
        ">
          <div style="
            display: flex;
            align-items: baseline;
            gap: ${isCompact ? '8px' : '12px'};
            flex-wrap: wrap;
            flex-shrink: 0;
          ">
            <span style="
              font-size: ${priceFontSize};
              font-weight: 600;
              color: ${designTokens.contentColor};
              line-height: 1.2;
            ">
              ${formatPrice(currentPrice)}
            </span>
            <span style="
              font-size: ${changeFontSize};
              color: ${changeColor};
              font-weight: 500;
              white-space: nowrap;
            ">
              ${isPositive ? '+' : ''}${formatPrice(Math.abs(change))} (${formatPercent(changePercent)})
            </span>
            ${!isCompact ? `
              <span style="
                color: ${designTokens.contentColorMuted};
                font-size: ${designTokens.fontSize.sm};
              ">
                ${dateStr}
              </span>
            ` : ''}
          </div>
          ${!shouldShowChart && miniChartHTML ? `
            <div style="
              display: flex;
              align-items: center;
              flex: 1 1 auto;
              margin-left: 12px;
              min-width: 120px;
              max-width: 500px;
            ">
              ${miniChartHTML}
            </div>
          ` : ''}
        </div>
        ${afterHoursPrice !== undefined && !isCompact ? `
          <div style="
            margin-top: ${isCompact ? '4px' : '8px'};
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.xs};
          ">
            After Hours: ${formatPrice(afterHoursPrice)}
            ${afterHoursChange !== undefined ? `
              <span style="color: ${afterHoursChange >= 0 ? '#00c853' : '#ff3b30'};">
                ${afterHoursChange >= 0 ? '+' : ''}${formatPrice(Math.abs(afterHoursChange))} 
                (${afterHoursChangePercent !== undefined ? formatPercent(afterHoursChangePercent) : ''})
              </span>
            ` : ''}
          </div>
        ` : ''}
      </div>
      
      <!-- Time Period Buttons -->
      <div style="
        display: flex;
        gap: ${isCompact ? '3px' : '4px'};
        margin-bottom: ${isCompact ? '8px' : '12px'};
        flex-wrap: wrap;
        flex-shrink: 0;
      ">
        ${periodButtons}
      </div>
      
      <!-- Chart (only show in state 3: double width + full height) -->
      ${shouldShowChart ? `
      <div style="
        position: relative;
        width: 100%;
        flex: 1;
        min-height: 0;
        margin-bottom: ${isCompact ? '12px' : '16px'};
      ">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 ${width} ${height}"
          preserveAspectRatio="xMidYMid meet"
          style="
            overflow: visible;
          "
        >
          <!-- Grid lines -->
          ${horizontalGridLines}
          
          <!-- Chart area background -->
          <rect
            x="${padding.left}"
            y="${padding.top}"
            width="${actualChartWidth}"
            height="${actualChartHeight}"
            fill="transparent"
          />
          
          <!-- Price line -->
          ${linePath ? `
            <path
              d="${linePath}"
              fill="none"
              stroke="${changeColor}"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          ` : ''}
          
          <!-- Time axis labels -->
          ${timeLabels}
          
          <!-- Y-axis label -->
          <text
            x="${padding.left / 2}"
            y="${padding.top + actualChartHeight / 2}"
            text-anchor="middle"
            fill="${designTokens.contentColorMuted}"
            font-size="${designTokens.fontSize.xs}"
            transform="rotate(-90 ${padding.left / 2} ${padding.top + actualChartHeight / 2})"
          >
            Price (USD)
          </text>
        </svg>
      </div>
      ` : ''}
      
      <!-- Financial Metrics -->
      <div style="
        display: grid;
        grid-template-columns: ${isCompact ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
        gap: ${isCompact ? '8px' : '12px'};
        padding-top: ${isCompact ? '8px' : '12px'};
        border-top: 1px solid ${designTokens.borderColor};
        flex-shrink: 0;
      ">
        <div>
          <div style="
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.xs};
            margin-bottom: 4px;
          ">
            Open
          </div>
          <div style="
            color: ${designTokens.contentColor};
            font-size: ${designTokens.fontSize.sm};
            font-weight: 500;
          ">
            ${formatPrice(open)}
          </div>
        </div>
        <div>
          <div style="
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.xs};
            margin-bottom: 4px;
          ">
            Volume
          </div>
          <div style="
            color: ${designTokens.contentColor};
            font-size: ${designTokens.fontSize.sm};
            font-weight: 500;
          ">
            ${formatVolume(volume)}
          </div>
        </div>
        <div>
          <div style="
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.xs};
            margin-bottom: 4px;
          ">
            Day Range
          </div>
          <div style="
            color: ${designTokens.contentColor};
            font-size: ${designTokens.fontSize.sm};
            font-weight: 500;
          ">
            ${formatPrice(dayLow)} - ${formatPrice(dayHigh)}
          </div>
        </div>
        <div>
          <div style="
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.xs};
            margin-bottom: 4px;
          ">
            Day Low
          </div>
          <div style="
            color: ${designTokens.contentColor};
            font-size: ${designTokens.fontSize.sm};
            font-weight: 500;
          ">
            ${formatPrice(dayLow)}
          </div>
        </div>
        <div>
          <div style="
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.xs};
            margin-bottom: 4px;
          ">
            Day High
          </div>
          <div style="
            color: ${designTokens.contentColor};
            font-size: ${designTokens.fontSize.sm};
            font-weight: 500;
          ">
            ${formatPrice(dayHigh)}
          </div>
        </div>
        <div>
          <div style="
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.xs};
            margin-bottom: 4px;
          ">
            Year Range
          </div>
          <div style="
            color: ${designTokens.contentColor};
            font-size: ${designTokens.fontSize.sm};
            font-weight: 500;
          ">
            ${formatPrice(yearLow)} - ${formatPrice(yearHigh)}
          </div>
        </div>
      </div>
    </div>
  `;
};

